import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import Modal from './Modal'
import PostDetailModal from './PostDetailModal'
import { HITOS, fmtFecha } from '../utils/aprobaciones'
import { fetchPendientesEnvio, urgenciaValidacion } from '../utils/pendientesEnvio'
import './VencidosModal.css' // clases .vm-* compartidas
import './AprobacionesModal.css' // clases .am-* compartidas

const PAGE_SIZE = 25
const hoyStr = () => new Date().toLocaleDateString('en-CA') // YYYY-MM-DD en hora local

const snoozeKey = (user) => `geves_pendientes_envio_modal_${user?.id ?? user?.username ?? 'anon'}`
const getSnooze = (user) => { try { return localStorage.getItem(snoozeKey(user)) } catch { return null } }
const setSnooze = (user) => { try { localStorage.setItem(snoozeKey(user), hoyStr()) } catch { /* sin storage: se mostrará de nuevo */ } }

const HITOS_ORDEN = ['idea_creativa', 'copy', 'contenido', 'contenido_rechazado']

/**
 * Modal de posts con validación próxima (hoy, mañana o ya pasada) a los que aún les falta
 * enviar a aprobación idea creativa, copy o contenido.
 *  - Paso 1 (alert): resumen con el total y botón "Ver detalle".
 *  - Paso 2 (detail): listado ordenado por urgencia; "Abrir" lleva al detalle del post,
 *    donde el editor envía el contenido.
 * Los datos los filtra el backend (/pendientes-envio/). Se muestra como máximo una vez al día.
 */
export default function PendientesEnvioModal({ user, enabled = true, ready = true, onChanged, onDone }) {
  const [step, setStep] = useState('closed') // 'closed' | 'alert' | 'detail'
  const [posts, setPosts] = useState([])
  const [alcance, setAlcance] = useState('mios')
  const [q, setQ] = useState('')
  const [hito, setHito] = useState('')
  const [urgencia, setUrgencia] = useState('')
  const [soloMios, setSoloMios] = useState(false)
  const [visible, setVisible] = useState(PAGE_SIZE)
  const [postModalId, setPostModalId] = useState(null)
  const changedRef = useRef(false)
  const startedRef = useRef(false)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  // ── Carga inicial (una sola vez, cuando es su turno): solo si no se descartó hoy ──
  useEffect(() => {
    if (!ready || startedRef.current) return
    startedRef.current = true
    if (!enabled || getSnooze(user) === hoyStr()) {
      if (onDoneRef.current) onDoneRef.current()
      return
    }
    fetchPendientesEnvio()
      .then(({ posts: list, alcance: a }) => {
        if (list.length === 0) { if (onDoneRef.current) onDoneRef.current(); return }
        setPosts(list)
        setAlcance(a)
        setStep('alert')
      })
      .catch(() => { if (onDoneRef.current) onDoneRef.current() }) // silencioso
  }, [ready, enabled, user])

  const close = useCallback(() => {
    setSnooze(user)
    setStep('closed')
    if (changedRef.current) {
      changedRef.current = false
      if (onChanged) onChanged()
    }
    if (onDoneRef.current) onDoneRef.current()
  }, [user, onChanged])

  useEffect(() => {
    if (step === 'closed' || postModalId) return
    const onKey = (e) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [step, postModalId, close])

  const recargar = async () => {
    try {
      const { posts: list, alcance: a } = await fetchPendientesEnvio()
      setPosts(list)
      setAlcance(a)
    } catch { /* se mantiene la lista actual */ }
  }

  // ── Derivados ────────────────────────────────────────────────────────────
  const conteos = useMemo(() => {
    const c = { idea_creativa: 0, copy: 0, contenido: 0, contenido_rechazado: 0 }
    posts.forEach(p => p.faltantes.forEach(h => { if (c[h] !== undefined) c[h] += 1 }))
    return c
  }, [posts])

  const atrasadas = useMemo(() => posts.filter(p => p.dias_para_validacion < 0).length, [posts])
  const mios = useMemo(() => posts.filter(p => p.soy_editor).length, [posts])

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase()
    return posts.filter(p =>
      (!hito || p.faltantes.includes(hito)) &&
      (!urgencia || urgenciaValidacion(p.dias_para_validacion).clave === urgencia) &&
      (!soloMios || p.soy_editor) &&
      (!t ||
        (p.titulo || '').toLowerCase().includes(t) ||
        (p.cliente || '').toLowerCase().includes(t) ||
        (p.influencer || '').toLowerCase().includes(t) ||
        (p.plataforma || '').toLowerCase().includes(t) ||
        p.editores.some(e => e.nombre.toLowerCase().includes(t)))
    )
  }, [posts, q, hito, urgencia, soloMios])

  useEffect(() => { setVisible(PAGE_SIZE) }, [q, hito, urgencia, soloMios])

  const shown = filtered.slice(0, visible)

  if (step === 'closed') return null

  const total = posts.length
  const plural = total === 1 ? '' : 's'
  const esManager = alcance === 'todos'

  return (
    <>
      {/* ── Modal 1: aviso ── */}
      <Modal
        isOpen={step === 'alert'}
        onClose={close}
        minimal
        size="sm"
        title={`Tienes ${total} post${plural} por enviar a aprobación`}
        footer={(
          <>
            <button className="g-btn g-btn-secondary" onClick={close}>Ahora no</button>
            <button className="g-btn g-btn-primary" onClick={() => setStep('detail')}>Ver detalle</button>
          </>
        )}
      >
        <p className="vm-lead">
          Existe{total === 1 ? '' : 'n'} <strong>{total} post{plural}</strong> con fecha de validación
          para hoy, mañana o ya pasada, a los que todavía les falta enviar a aprobación idea creativa, copy o contenido
          (o tienen archivos rechazados por reemplazar).
          {atrasadas > 0 && (<> <strong>{atrasadas}</strong> ya {atrasadas === 1 ? 'tiene' : 'tienen'} la validación atrasada.</>)}
          {esManager && mios > 0 && (<> De ellos, <strong>{mios}</strong> {mios === 1 ? 'es' : 'son'} tuyo{mios === 1 ? '' : 's'} como editor.</>)}
        </p>
        <div className="vm-breakdown">
          <span className="vm-breakdown-title">Lo que falta enviar</span>
          <div className="vm-chips">
            {HITOS_ORDEN.map(h => (
              <span key={h} className="vm-chip">
                <i className={`bi ${HITOS[h].icon}`} style={{ color: HITOS[h].color }} /> {HITOS[h].label} <strong>{conteos[h]}</strong>
              </span>
            ))}
          </div>
        </div>
      </Modal>

      {/* ── Modal 2: detalle ── */}
      <Modal
        isOpen={step === 'detail' && !postModalId}
        onClose={close}
        minimal
        size="xl"
        title={`Pendientes de enviar a aprobación · ${total}`}
        subtitle="Ordenados por fecha de validación. Abre el post para enviar lo que falta."
        footerAlignment="between"
        footer={(
          <>
            <span className="vm-foot-info">
              Mostrando {shown.length} de {filtered.length}
              {filtered.length !== total ? ` (${total} en total)` : ''} · validación más urgente primero
            </span>
            <div className="vm-foot-actions">
              <button className="g-btn g-btn-primary" onClick={close}>Cerrar</button>
            </div>
          </>
        )}
      >
        <div className="vm-toolbar">
          <input
            className="vm-input"
            type="search"
            placeholder="Buscar por título, cliente, cuenta, plataforma o editor…"
            aria-label="Buscar post"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          <select className="vm-select" aria-label="Filtrar por lo que falta" value={hito} onChange={e => setHito(e.target.value)}>
            <option value="">Todo lo que falta</option>
            {HITOS_ORDEN.map(h => (
              <option key={h} value={h}>
                {h === 'contenido_rechazado' ? HITOS[h].label : `Falta ${HITOS[h].label}`} ({conteos[h]})
              </option>
            ))}
          </select>
          <select className="vm-select" aria-label="Filtrar por urgencia" value={urgencia} onChange={e => setUrgencia(e.target.value)}>
            <option value="">Toda validación</option>
            <option value="atrasada">Atrasada</option>
            <option value="hoy">Hoy</option>
            <option value="manana">Mañana</option>
          </select>
          {esManager && (
            <label className="am-check">
              <input type="checkbox" checked={soloMios} onChange={e => setSoloMios(e.target.checked)} />
              Solo mis posts como editor ({mios})
            </label>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="g-empty">
            <i className="bi bi-check2-circle g-empty-icon" />
            <p className="g-empty-title">{total === 0 ? 'Nada pendiente de enviar' : 'Ningún post coincide con los filtros'}</p>
          </div>
        ) : (
          <div className="vm-scroll">
            <table className="g-table vm-table">
              <thead>
                <tr>
                  <th>Post</th>
                  <th>Plataforma</th>
                  <th>Validación</th>
                  <th>Publicación</th>
                  <th style={{ minWidth: 260 }}>Falta enviar</th>
                  <th>Editor</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map(p => {
                  const u = urgenciaValidacion(p.dias_para_validacion)
                  return (
                    <tr key={p.id}>
                      <td style={{ maxWidth: 240, verticalAlign: 'top' }}>
                        <div className="am-post-title">{p.titulo}</div>
                        <div className="am-sub">
                          {[p.influencer, p.cliente, p.status_display].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td style={{ verticalAlign: 'top' }}>{p.plataforma || '—'}</td>
                      <td className="am-fecha" style={{ verticalAlign: 'top' }}>
                        {fmtFecha(p.fecha_validacion)}
                        <div className={`am-dias am-dias-${u.nivel}`}>{u.texto}</div>
                      </td>
                      <td className="am-fecha" style={{ verticalAlign: 'top' }}>{fmtFecha(p.fecha_publicacion)}</td>
                      <td style={{ verticalAlign: 'top' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {HITOS_ORDEN.filter(h => p.faltantes.includes(h)).map(h => {
                            const info = HITOS[h]
                            const bloqueado = h === 'copy' && !p.idea_aprobada
                            return (
                              <span
                                key={h}
                                className="am-chip"
                                style={{ color: info.color, background: info.bg }}
                                title={bloqueado ? 'El copy solo se puede enviar cuando la idea creativa está aprobada' : undefined}
                              >
                                <i className={`bi ${info.icon}`} /> {info.label}
                                {bloqueado && <i className="bi bi-lock-fill" aria-label="Requiere idea creativa aprobada" />}
                              </span>
                            )
                          })}
                        </div>
                      </td>
                      <td style={{ verticalAlign: 'top', fontSize: 13 }}>
                        {p.editores.length === 0
                          ? <span className="am-meta">Sin editor</span>
                          : p.editores.map(e => e.nombre).join(', ')}
                      </td>
                      <td style={{ verticalAlign: 'top' }}>
                        <button className="g-btn g-btn-secondary g-btn-sm" onClick={() => setPostModalId(p.id)}>
                          Abrir
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {filtered.length > visible && (
              <div className="vm-more">
                <button className="g-btn g-btn-secondary" onClick={() => setVisible(v => v + PAGE_SIZE)}>
                  Cargar {Math.min(PAGE_SIZE, filtered.length - visible)} más
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Detalle completo del post, donde el editor envía el contenido */}
      {postModalId && (
        <PostDetailModal
          postId={postModalId}
          onClose={() => { setPostModalId(null); changedRef.current = true; recargar() }}
          onUpdate={() => { changedRef.current = true; recargar() }}
        />
      )}
    </>
  )
}
